import type { Query, Transaction } from "@rocicorp/zero";
import { advisoryLock } from "../db/advisoryLock";
import { userFacingError } from "./authorization";
import { type CommitRoute, type Schema, zql } from "./generatedDrizzleSchema";
import { DEFAULT_PROVIDER_IDS } from "./ssoProviderIds";

function dependentRoutes(providerId: string | null, includeInherited: boolean) {
  return (r: Query<"commitRoutes", Schema, CommitRoute>) =>
    r.where("routeAuth", "sso").where(({ or, cmp }) => {
      const inherited = or(cmp("routeIdp", "IS", null), cmp("routeIdp", ""));
      if (providerId === null) return inherited;
      return includeInherited
        ? or(cmp("routeIdp", providerId), inherited)
        : cmp("routeIdp", providerId);
    });
}

export function ssoRouteDependentBranches(
  tenantId: string,
  providerId: string | null,
  includeInherited: boolean
) {
  const routes = dependentRoutes(providerId, includeInherited);
  return zql.workflowBranches
    .where("tenantId", tenantId)
    .whereExists("workflow", (w) => w.where("deletedAt", "IS", null))
    .whereExists("headCommit", (c) => c.whereExists("routes", routes));
}

export function ssoRouteDependenciesQuery(
  tenantId: string,
  providerId: string | null,
  includeInherited: boolean
) {
  const routes = dependentRoutes(providerId, includeInherited);
  return ssoRouteDependentBranches(tenantId, providerId, includeInherited)
    .related("workflow", (w) => w.related("space"))
    .related("headCommit", (c) => c.related("routes", routes));
}

export async function lockSsoRouteProviders(
  tx: Transaction<Schema>,
  tenantId: string,
  mode: "exclusive" | "shared" = "exclusive"
) {
  if (tx.location === "server") {
    await advisoryLock(
      tx.dbTransaction.wrappedTransaction,
      `sso-route-providers:${tenantId}`,
      mode
    );
  }
}

export async function assertSsoRouteProviders(
  tx: Transaction<Schema>,
  tenantId: string,
  commitId: string
) {
  if (tx.location !== "server") return;
  const routes = await tx.run(
    zql.commitRoutes
      .where("tenantId", tenantId)
      .where("commitId", commitId)
      .where("routeAuth", "sso")
      .where("routeIdp", "IS NOT", null)
      .where("routeIdp", "!=", "")
  );
  if (routes.length === 0) return;
  const providers = await tx.run(zql.ssoProvider.where("tenantId", tenantId));
  const unregistered = routes.find(
    (r) =>
      DEFAULT_PROVIDER_IDS.has(r.routeIdp!) ||
      !providers.some((p) => p.providerId === r.routeIdp)
  );
  if (unregistered) {
    throw userFacingError(
      `Step “${unregistered.stepDir}” uses unregistered SSO provider “${unregistered.routeIdp}” in route_idp. Register a provider with that ID, or change route_idp.`
    );
  }
}
